import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { fetchDocsAsset, isExternalDocsAssetUrl, resolveDocsAssetPath } from '../../../Api/Docs';

interface DocsImageProps {
  src?: string;
  alt?: string;
  title?: string;
  simulateRole?: string;
}

// Renders an image referenced from docs Markdown. Docs assets are not publicly
// reachable — they are served by GET /api/docs/asset/*path behind the same
// Bearer auth as the rest of the API — so a plain <img src> cannot load them.
// For internal assets we fetch the bytes through the authenticated API layer and
// render them via an object URL; external/inline (http/data) images load
// directly. The object URL is revoked on unmount/src change to avoid leaks.
export const DocsImage: FunctionComponent<DocsImageProps> = ({ src, alt, title, simulateRole }) => {
  const isExternal = !!src && isExternalDocsAssetUrl(src);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<boolean>(false);

  useEffect(() => {
    if (!src || isExternal) return;

    const controller = new AbortController();
    let createdUrl: string | null = null;
    setObjectUrl(null);
    setFailed(false);

    fetchDocsAsset(resolveDocsAssetPath(src), simulateRole, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      if (result.error || !result.data) {
        setFailed(true);
        return;
      }
      createdUrl = URL.createObjectURL(result.data);
      setObjectUrl(createdUrl);
    });

    return () => {
      controller.abort();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [src, isExternal, simulateRole]);

  if (!src || failed) {
    return (
      <Typography
        component="span"
        variant="body2"
        sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        {alt || 'Image unavailable'}
      </Typography>
    );
  }

  const resolvedSrc = isExternal ? src : objectUrl;
  if (!resolvedSrc) {
    // Asset bytes are still loading. Use an inline span so this is valid DOM
    // even when react-markdown wraps a lone image in a <p>.
    return (
      <Box
        component="span"
        sx={{ display: 'inline-block', minWidth: 80, minHeight: 24 }}
        aria-hidden="true"
      />
    );
  }

  // Use phrasing content (span elements) rather than a <figure>. react-markdown
  // wraps a lone image in a <p>, and <p> may only contain phrasing content — a
  // nested <figure> would be auto-closed by the browser, producing invalid DOM
  // and inconsistent spacing. role="figure" preserves the figure semantics.
  return (
    <Box
      component="span"
      role="figure"
      sx={{ display: 'block', maxWidth: '100%', my: 2, mx: 0 }}>
      <Box
        component="img"
        src={resolvedSrc}
        alt={alt || ''}
        title={title}
        sx={{
          maxWidth: '100%',
          maxHeight: 500,
          height: 'auto',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          display: 'block',
          mx: 'auto'
        }}
      />
      {(title || alt) && (
        <Typography
          component="span"
          variant="caption"
          sx={{ display: 'block', mt: 0.5, color: 'text.secondary', textAlign: 'center' }}>
          {title || alt}
        </Typography>
      )}
    </Box>
  );
};
